import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import idPhoto from "../Assets/Images/idPhoto.png";
import CustomSelect from "../CustomSelect";
import PopUp from "../PopUp";

const EditUser = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    descrizione: "",
    tipologia: "",
  });
  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sedi, setSedi] = useState([]);
  const [selectedSedeIds, setSelectedSedeIds] = useState([]);
  const [sediLoaded, setSediLoaded] = useState(false);

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const currentUserRole = localStorage.getItem("userRole");

  const fetchSedi = async () => {
    try {
      const response = await api.get(`/sedi`);
      if (response.status === 200) {
        setSedi(response.data);
        setSediLoaded(true);
      } else {
        setSediLoaded(true);
        toast.error("Errore nel caricamento delle sedi.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      setSediLoaded(true);
      toast.error(error?.response?.data?.message || "Errore nel caricamento delle sedi.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per modificare l'utente!");
      navigate("/login");
      localStorage.clear();
      return;
    }
    
    fetchSedi();
    
    const targetUser = location.state?.user;

    if (targetUser) {
      setUser(targetUser);
      setForm({
        id: targetUser.id,
        nome: targetUser.nome || "",
        cognome: targetUser.cognome || "",
        descrizione: targetUser.descrizione || "",
        sedi: targetUser.sedi,
        tipologia: targetUser.tipologia || "",
      });
      setSelectedUserId(targetUser.id);
    } else {
      toast.error("Dati utente non trovati.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
    setLoading(false);
  }, [location.state, navigate]);

  // Initialize selected sede IDs when both user and sedi are available
  useEffect(() => {
    if (!user || !sediLoaded) return;

    // First, try to initialize from sediOperatori (array of strings - sede addresses)
    if (user.sediOperatori && Array.isArray(user.sediOperatori) && user.sediOperatori.length > 0 && sedi.length > 0) {
      // Match sediOperatori strings with sedi list to get IDs
      const sedeIds = user.sediOperatori
        .map((sedeAddress) => {
          const foundSede = sedi.find((s) => s.indirizzoSede === sedeAddress);
          return foundSede ? foundSede.idSede : null;
        })
        .filter((id) => id !== null && id !== undefined);
      
      if (sedeIds.length > 0) {
        setSelectedSedeIds(sedeIds);
        return;
      }
    }

    // Fallback: Initialize selected sede IDs from user.sedi
    // Handle both array of objects and array of IDs
    if (user.sedi && Array.isArray(user.sedi) && user.sedi.length > 0) {
      const sedeIds = user.sedi.map((sede) => {
        if (typeof sede === 'object' && sede !== null) {
          // If it's an object, try to get idSede or id
          return sede.idSede || sede.id;
        } else {
          // If it's already an ID (number), use it directly
          return sede;
        }
      }).filter((id) => id !== null && id !== undefined); // Remove any undefined/null values
      
      // Only set if we found valid IDs
      if (sedeIds.length > 0) {
        setSelectedSedeIds(sedeIds);
      } else {
        setSelectedSedeIds([]);
      }
    } else if (user.sedi && typeof user.sedi === 'string' && sedi.length > 0) {
      // If it's a string, try to match by indirizzoSede
      const sedeIds = sedi
        .filter((sede) => user.sedi.includes(sede.indirizzoSede))
        .map((sede) => sede.idSede);
      setSelectedSedeIds(sedeIds.length > 0 ? sedeIds : []);
    } else {
      setSelectedSedeIds([]);
    }
  }, [user, sedi, sediLoaded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const inputErrors = {};

    if (!form.nome.trim()) {
      inputErrors.nome = "Il campo Nome non può essere vuoto!";
    }

    if (!form.cognome.trim()) {
      inputErrors.cognome = "Il campo Cognome non può essere vuoto!";
    }

    if (currentUserRole === "ADMIN") {
      if (!["Administrator", "Operatore"].includes(form.tipologia)) {
        inputErrors.tipologia = "Valore non valido per il campo Tipologia!";
      }
    }

    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = {
        nome: form.nome,
        cognome: form.cognome,
        descrizione: form.descrizione,
        sedeIds: selectedSedeIds,
      };

      if (currentUserRole === "ADMIN") {
        payload.tipologia = form.tipologia;
      }

      const queryParam = currentUserRole === "ADMIN" && id ? `?id=${id}` : "";
      const response = await api.put(`/users${queryParam}`, payload);

      if (response.status === 200) {
        toast.success("Utente aggiornato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        
        // Update user state with response data, including sediOperatori
        const updatedUser = {
          ...user,
          ...form,
          sediOperatori: response.data?.sediOperatori || user.sediOperatori,
        };
        setUser(updatedUser);
        
        // Update selectedSedeIds based on sediOperatori from response
        if (response.data?.sediOperatori && Array.isArray(response.data.sediOperatori) && sedi.length > 0) {
          const sedeIds = response.data.sediOperatori
            .map((sedeAddress) => {
              const foundSede = sedi.find((s) => s.indirizzoSede === sedeAddress);
              return foundSede ? foundSede.idSede : null;
            })
            .filter((id) => id !== null && id !== undefined);
          
          if (sedeIds.length > 0) {
            setSelectedSedeIds(sedeIds);
          }
        }
        
        setEditMode(false);
      } else {
        toast.error("Errore durante l'aggiornamento.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setForm({
        nome: user.nome || "",
        cognome: user.cognome || "",
        descrizione: user.descrizione || "",
        tipologia: user.tipologia || "OPERATORE",
      });
      // Reset selected sede IDs using the same logic as initialization
      if (user.sedi && Array.isArray(user.sedi)) {
        const sedeIds = user.sedi.map((sede) => {
          if (typeof sede === 'object') {
            return sede.idSede || sede.id;
          } else {
            return sede;
          }
        }).filter(Boolean);
        setSelectedSedeIds(sedeIds);
      } else if (user.sedi && typeof user.sedi === 'string' && sedi.length > 0) {
        // If it's a string, try to match by indirizzoSede
        const sedeIds = sedi
          .filter((sede) => user.sedi.includes(sede.indirizzoSede))
          .map((sede) => sede.idSede);
        setSelectedSedeIds(sedeIds);
      } else {
        setSelectedSedeIds([]);
      }
    }
    setErrors({});
    setEditMode(false);
  };

  const handleRemoveSede = (sedeIdToRemove) => {
    setSelectedSedeIds((prev) => prev.filter((id) => id !== sedeIdToRemove));
  };

  const handleAddSede = (selectedOptions) => {
    if (selectedOptions) {
      const newSedeIds = selectedOptions.map((option) => option.value);
      setSelectedSedeIds(newSedeIds);
      setErrors((prev) => ({ ...prev, sedi: "" }));
    } else {
      setSelectedSedeIds([]);
      setErrors((prev) => ({ ...prev, sedi: "" }));
    }
  };

  const toggleUserActivity = async () => {
    if (!selectedUserId) return;

    try {
      setLoading(true);

      const response = await api.patch(`/users/${selectedUserId}/disable`);

      if (response.status === 200) {
        toast.success("L'utente è stato disabilitato con successo!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        });

        setUser((prev) => ({ ...prev, enabled: response.data.enabled }));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
      setSelectedUserId(null);
      setShowPopUp(false);
    }
  };

  const requestFeedback = async (email) => {
    try {
      setLoading(true);
      const response = await api.post(`/feedback/email?recipient=${email}`);
      if (response.status === 200) {
        toast.success(
          `Email di richiesta di feedback inviata correttamente a ${email}`,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          }
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const reactivateUser = async (email) => {
    try {
      setLoading(true);

      const response = await api.post("/auth/resend-activation", { email });
      if (response.status === 200) {
        toast.success(
          "L'email di attivazione dell'account è stata inviata con successo!",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          }
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  if (loading) {
    return (
      <div className="edit-user-overlay">
        <div className="edit-user-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="edit-user-container">
        <div className="edit-user-error">
          <h2>Utente non trovato</h2>
          <p>Impossibile caricare i dati dell'utente.</p>
          <button
            onClick={() => navigate("/utenti")}
            className="edit-user-back-btn"
          >
            <i className="fa-solid fa-arrow-left"></i>
            Torna alla lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-user-page">
      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma disabilitazione utente"
        content="Sei sicuro di voler disabilitare questo utente?"
        deleteFunction={toggleUserActivity}
      />

      {/* Header Section */}
      <div className="edit-user-header">
        <div className="edit-user-header-content">
          <button
            onClick={() => navigate("/utenti")}
            className="edit-user-back-button"
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>Torna alla lista</span>
          </button>
          <h1 className="edit-user-title">Modifica Utente</h1>
          <div className="edit-user-actions">
            {!editMode ? (
              <button
                className="edit-user-edit-btn"
                onClick={() => setEditMode(true)}
              >
                <i className="fa-solid fa-edit"></i>
                <span>Modifica</span>
              </button>
            ) : (
              <div className="edit-user-save-cancel">
                <button className="edit-user-cancel-btn" onClick={handleCancel}>
                  <i className="fa-solid fa-times"></i>
                  <span>Annulla</span>
                </button>
                <button className="edit-user-save-btn" onClick={handleSave}>
                  <i className="fa-solid fa-check"></i>
                  <span>Salva</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="edit-user-content">
        <div className="edit-user-card">
          {/* User Profile Section */}
          <div className="edit-user-profile">
            <div className="edit-user-avatar">
              <img src={idPhoto} alt="Avatar utente" />
              <div className="edit-user-status">
                <span
                  className={`edit-user-status-badge ${user?.tipologia?.toLowerCase()}`}
                >
                  {user?.tipologia === "ADMINISTRATOR"
                    ? "Amministratore"
                    : "Operatore"}
                </span>
              </div>
            </div>
            <div className="edit-user-info">
              <h2 className="edit-user-name">
                {user?.nome} {user?.cognome}
              </h2>
              <p className="edit-user-email">{user?.email}</p>
              <p className="edit-user-created">
                Creato il {formatDate(user?.dataCreazione)}
              </p>
            </div>
          </div>

          {/* User Details Section */}
          <div className="edit-user-details">
            <div className="edit-user-section">
              <h3 className="edit-user-section-title">
                Informazioni Personali
              </h3>
              <div className="edit-user-fields">
                <div className="edit-user-field">
                  <label className="edit-user-label">Nome</label>
                  {editMode ? (
                    <input
                      type="text"
                      className={`edit-user-input ${
                        errors.nome ? "error" : ""
                      }`}
                      value={form.nome}
                      onChange={handleChange}
                      name="nome"
                      placeholder="Inserisci il nome"
                    />
                  ) : (
                    <span className="edit-user-value">{user?.nome}</span>
                  )}
                  {errors.nome && (
                    <span className="edit-user-error">{errors.nome}</span>
                  )}
                </div>

                <div className="edit-user-field">
                  <label className="edit-user-label">Cognome</label>
                  {editMode ? (
                    <input
                      type="text"
                      className={`edit-user-input ${
                        errors.cognome ? "error" : ""
                      }`}
                      value={form.cognome}
                      onChange={handleChange}
                      name="cognome"
                      placeholder="Inserisci il cognome"
                    />
                  ) : (
                    <span className="edit-user-value">{user?.cognome}</span>
                  )}
                  {errors.cognome && (
                    <span className="edit-user-error">{errors.cognome}</span>
                  )}
                </div>

                <div className="edit-user-field">
                  <label className="edit-user-label">Descrizione</label>
                  {editMode ? (
                    <textarea
                      className={`edit-user-textarea ${
                        errors.descrizione ? "error" : ""
                      }`}
                      value={form.descrizione}
                      onChange={handleChange}
                      name="descrizione"
                      placeholder="Inserisci una descrizione"
                      rows="3"
                    />
                  ) : (
                    <span className="edit-user-value">
                      {user?.descrizione || "—"}
                    </span>
                  )}
                  {errors.descrizione && (
                    <span className="edit-user-error">
                      {errors.descrizione}
                    </span>
                  )}
                </div>

                <div className="edit-user-field">
                  <label className="edit-user-label">Tipologia</label>
                  {editMode ? (
                    <CustomSelect
                      options={[
                        { value: "Operatore", label: "Operatore" },
                        { value: "Administrator", label: "Administrator" },
                      ]}
                      value={
                        form.tipologia
                          ? { value: form.tipologia, label: form.tipologia }
                          : null
                      }
                      onChange={(selectedOption) => {
                        handleChange({
                          target: {
                            name: "tipologia",
                            value: selectedOption ? selectedOption.value : "",
                          },
                        });
                      }}
                      placeholder="Seleziona tipologia"
                    />
                  ) : (
                    <span className="edit-user-value">
                      {user?.tipologia === "ADMINISTRATOR"
                        ? "Amministratore"
                        : "Operatore"}
                    </span>
                  )}
                  {errors.tipologia && (
                    <span className="edit-user-error">{errors.tipologia}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Sedi Section */}
            <div className="edit-user-section">
              <h3 className="edit-user-section-title">Sedi Associate</h3>
              <div className="edit-user-sedi">
                {editMode ? (
                  <div className="edit-user-sedi-edit">
                    <CustomSelect
                      options={sedi.map((sede) => ({
                        value: sede.idSede,
                        label: sede.indirizzoSede,
                      }))}
                      value={
                        sedi.length > 0 && selectedSedeIds.length > 0
                          ? sedi
                              .filter((sede) => selectedSedeIds.includes(sede.idSede))
                              .map((sede) => ({
                                value: sede.idSede,
                                label: sede.indirizzoSede,
                              }))
                          : []
                      }
                      onChange={handleAddSede}
                      placeholder="Seleziona le sedi"
                      isMulti
                    />
                    {selectedSedeIds.length > 0 && (
                      <div className="edit-user-sedi-selected">
                        {selectedSedeIds.map((sedeId) => {
                          const sede = sedi.find((s) => s.idSede === sedeId);
                          if (!sede) return null;
                          return (
                            <span key={sedeId} className="edit-user-sede-item-removable">
                              {sede.indirizzoSede}
                              <button
                                type="button"
                                className="edit-user-sede-remove"
                                onClick={() => handleRemoveSede(sedeId)}
                                aria-label="Rimuovi sede"
                              >
                                <i className="fa-solid fa-times"></i>
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {(() => {
                      // First priority: Display sediOperatori if available
                      if (user?.sediOperatori && Array.isArray(user.sediOperatori) && user.sediOperatori.length > 0) {
                        return (
                          <div className="edit-user-sedi-list">
                            {user.sediOperatori.map((sedeAddress, index) => (
                              <span key={index} className="edit-user-sede-item">
                                {sedeAddress}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      
                      // Second priority: Try to display from selectedSedeIds if available and sedi are loaded
                      if (selectedSedeIds.length > 0 && sedi.length > 0) {
                        const foundSedi = selectedSedeIds
                          .map((sedeId) => sedi.find((s) => s.idSede === sedeId))
                          .filter(Boolean);
                        if (foundSedi.length > 0) {
                          return (
                            <div className="edit-user-sedi-list">
                              {foundSedi.map((sede) => (
                                <span key={sede.idSede} className="edit-user-sede-item">
                                  {sede.indirizzoSede}
                                </span>
                              ))}
                            </div>
                          );
                        }
                      }
                      
                      // Fallback to user.sedi
                      if (user?.sedi) {
                        return (
                          <div className="edit-user-sedi-list">
                            {typeof user.sedi === 'string' 
                              ? user.sedi.split(";").map((sede, index) => (
                                  <span key={index} className="edit-user-sede-item">
                                    {sede.trim()}
                                  </span>
                                ))
                              : Array.isArray(user.sedi) && user.sedi.map((sede, index) => (
                                  <span key={index} className="edit-user-sede-item">
                                    {typeof sede === 'object' ? sede.indirizzoSede : sede}
                                  </span>
                                ))
                            }
                          </div>
                        );
                      }
                      
                      // No sedes found
                      return (
                        <span className="edit-user-no-sedi">
                          Nessuna sede associata
                        </span>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {!editMode && (
          <div className="edit-user-actions-panel">
            <div className="edit-user-actions-grid">
              <button
                className="edit-user-action-btn feedback"
                onClick={() => requestFeedback(user.email)}
              >
                <i className="fa-solid fa-comment"></i>
                <span>Richiedi Feedback</span>
              </button>

              {user.enabled === false && (
                <button
                  className="edit-user-action-btn reactivate"
                  onClick={() => reactivateUser(user.email)}
                >
                  <i className="fa-solid fa-user-check"></i>
                  <span>Riattiva Utente</span>
                </button>
              )}

              {user.enabled === true && (
                <button
                  className="edit-user-action-btn delete"
                  onClick={() => {
                    setSelectedUserId(user.id);
                    setShowPopUp(true);
                  }}
                >
                  <i className="fa-solid fa-trash"></i>
                  <span>Disabilita Utente</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditUser;
